import { useEffect, useRef, useState } from "react";
import { adminFetch } from "../adminApi";

/* ছবির ঘর — URL লেখা যায় বা সরাসরি আপলোড */
export function ImageField({ value, onChange, label = "ছবি" }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const up = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await adminFetch("/admin/upload", { method: "POST", body: fd });
      onChange(r.url);
    } catch (err) {
      alert(err.message);
    }
    setBusy(false);
  };

  return (
    <label className="block">
      <span className="text-xs tracking-wide text-sand/60">{label}</span>
      <div className="mt-1 flex gap-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… বা আপলোড করুন"
          data-testid={`image-field-${label}`}
          className="w-full rounded-lg border border-gold/20 bg-dusk/60 px-3 py-2 text-sm text-sand outline-none transition-colors focus:border-gold/60"
        />
        <button
          type="button"
          data-testid={`image-upload-${label}`}
          onClick={() => fileRef.current?.click()}
          className="shrink-0 rounded-lg border border-gold/30 bg-gold/10 px-3 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk"
        >
          {busy ? "…" : "আপলোড"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={up} />
      </div>
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-gold/20 bg-dusk/60 px-3 py-2 text-sm text-sand outline-none transition-colors focus:border-gold/60";

export function Field({ label, value, onChange, testid, area }) {
  return (
    <label className="block">
      <span className="text-xs tracking-wide text-sand/60">{label}</span>
      {area ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          data-testid={testid}
          className={`mt-1 ${inputCls}`}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`mt-1 ${inputCls}`}
        />
      )}
    </label>
  );
}

export function SaveBar({ saving, done, onSave, testid }) {
  return (
    <div className="mt-5 flex items-center gap-3">
      <button
        data-testid={testid}
        onClick={onSave}
        disabled={saving}
        className="rounded-full bg-gold px-6 py-2 text-sm font-medium text-dusk transition-transform hover:scale-[1.03] disabled:opacity-50"
      >
        {saving ? "সাধছি…" : "সাধুন"}
      </button>
      {done && <span data-testid={`${testid}-done`} className="text-sm text-emerald-400">সাধা হয়েছে ✓</span>}
    </div>
  );
}

/* ---------- সাইট-কনটেন্ট ---------- */
export function SiteContent({ data, refresh }) {
  const [f, setF] = useState(data.settings);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => setF(data.settings), [data]);

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch("/admin/site", { method: "PUT", body: JSON.stringify(f) });
      setDone(true);
      refresh();
      setTimeout(() => setDone(false), 2500);
    } catch (e) {
      alert(e.message);
    }
    setSaving(false);
  };

  const lines = (arr) => (arr || []).join("\n");
  const toLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-section-content">
      <Field label="গ্রামের নাম" value={f.brand_title || ""} onChange={(v) => setF({ ...f, brand_title: v })} testid="field-brand-title" />
      <Field label="আইরিন-লাইন (বাংলা ট্যাগলাইন)" value={f.tagline || ""} onChange={(v) => setF({ ...f, tagline: v })} testid="field-tagline" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="ইংরেজি ট্যাগলাইন (দিন)" value={f.latin_tagline || ""} onChange={(v) => setF({ ...f, latin_tagline: v })} testid="field-latin-day" />
        <Field label="ইংরেজি ট্যাগলাইন (সন্ধ্যা)" value={f.latin_evening || ""} onChange={(v) => setF({ ...f, latin_evening: v })} testid="field-latin-evening" />
      </div>

      <Field label="দিনের স্বাগতম-বাক্য (প্রতি লাইনে একটি)" value={lines(f.welcome_day)} onChange={(v) => setF({ ...f, welcome_day: toLines(v) })} testid="field-welcome-day" area />
      <Field label="সন্ধ্যার স্বাগতম-বাক্য (প্রতি লাইনে একটি)" value={lines(f.welcome_evening)} onChange={(v) => setF({ ...f, welcome_evening: toLines(v) })} testid="field-welcome-evening" area />
      <Field label="মার্কি-ব্যান্ড (কমা দিয়ে আলাদা)" value={(f.marquee || []).join(", ")} onChange={(v) => setF({ ...f, marquee: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="field-marquee" area />

      <div className="grid gap-4 sm:grid-cols-2">
        <ImageField label="লোগো" value={f.logo_url} onChange={(v) => setF({ ...f, logo_url: v })} />
        <ImageField label="গেট — দিন" value={f.gate_day} onChange={(v) => setF({ ...f, gate_day: v })} />
        <ImageField label="গেট — সন্ধ্যা" value={f.gate_dusk} onChange={(v) => setF({ ...f, gate_dusk: v })} />
        <ImageField label="গেট — রাত" value={f.gate_night} onChange={(v) => setF({ ...f, gate_night: v })} />
      </div>

      <SaveBar saving={saving} done={done} onSave={save} testid="save-site-content" />
    </div>
  );
}

/* ---------- হাট ও দোকান ---------- */
export function Shops({ data, refresh }) {
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(null);

  const startNew = () => setForm({ key: "", name: "", latin: "", blurb: "", image: "", order: (data.shops.length + 1) * 10, active: true });
  const startEdit = (s) => { setEdit(s.key); setForm({ ...s }); };

  const save = async () => {
    try {
      if (edit) await adminFetch(`/admin/shops/${edit}`, { method: "PUT", body: JSON.stringify(form) });
      else await adminFetch("/admin/shops", { method: "POST", body: JSON.stringify(form) });
      setForm(null); setEdit(null); refresh();
    } catch (e) { alert(e.message); }
  };

  const del = async (key) => {
    if (!window.confirm(`"${key}" দোকান আর তার সব পণ্য মুছে যাবে?`)) return;
    await adminFetch(`/admin/shops/${key}`, { method: "DELETE" });
    refresh();
  };

  return (
    <div className="max-w-3xl" data-testid="admin-section-shops">
      <button data-testid="add-shop" onClick={startNew} className="mb-5 rounded-full bg-gold px-5 py-2 text-sm font-medium text-dusk transition-transform hover:scale-[1.03]">নতুন দোকান</button>
      <div className="space-y-3">
        {data.shops.map((s) => (
          <div key={s.key} className="flex items-center gap-4 rounded-xl border border-gold/15 bg-dusk/50 p-4" data-testid={`shop-row-${s.key}`}>
            <img src={s.image} alt="" className="h-12 w-16 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg text-sand">{s.name}</p>
              <p className="truncate text-xs text-sand/50">{s.key} • {s.latin}</p>
            </div>
            <button data-testid={`edit-shop-${s.key}`} onClick={() => startEdit(s)} className="rounded-full border border-gold/30 px-4 py-1 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk">সাধুন</button>
            <button data-testid={`del-shop-${s.key}`} onClick={() => del(s.key)} className="rounded-full border border-red-400/30 px-4 py-1 text-xs text-red-300 transition-colors hover:bg-red-400 hover:text-dusk">মুছুন</button>
          </div>
        ))}
      </div>

      {form && (
        <div className="mt-6 space-y-4 rounded-xl border border-gold/25 bg-plaster p-5">
          <h4 className="font-display text-lg text-gold">{edit ? `সাধুন — ${edit}` : "নতুন দোকান"}</h4>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="চিহ্ন (ইংরেজি, যেমন pottery)" value={form.key} onChange={(v) => setForm({ ...form, key: v })} testid="shop-key" />
            <Field label="নাম" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="shop-name" />
            <Field label="ইংরেজি পরিচয়" value={form.latin} onChange={(v) => setForm({ ...form, latin: v })} testid="shop-latin" />
            <Field label="ক্রম" value={String(form.order)} onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} testid="shop-order" />
          </div>
          <Field label="পরিচয়-বাক্য" value={form.blurb} onChange={(v) => setForm({ ...form, blurb: v })} testid="shop-blurb" area />
          <ImageField label="দোকানের ছবি" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
          <SaveBar saving={false} done={false} onSave={save} testid="save-shop" />
          <button onClick={() => { setForm(null); setEdit(null); }} className="ml-3 text-sm text-sand/60 hover:text-sand">বাতিল</button>
        </div>
      )}
    </div>
  );
}

/* ---------- পণ্য ---------- */
export function Products({ data, refresh }) {
  const [shopKey, setShopKey] = useState(data.shops[0]?.key || "");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const list = data.products.filter((p) => p.shop_key === shopKey);

  const startNew = () => { setEditId(null); setForm({ shop_key: shopKey, title: "", latin: "", price: "", artisan: "", img: "", tag: "", order: (list.length + 1) * 10, active: true }); };
  const startEdit = (p) => { setEditId(p.id); setForm({ ...p, shop_key: shopKey }); };

  const save = async () => {
    try {
      if (editId) await adminFetch(`/admin/products/${editId}`, { method: "PUT", body: JSON.stringify(form) });
      else await adminFetch("/admin/products", { method: "POST", body: JSON.stringify(form) });
      setForm(null); setEditId(null); refresh();
    } catch (e) { alert(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("পণ্যটি মুছে যাবে?")) return;
    await adminFetch(`/admin/products/${id}`, { method: "DELETE" });
    refresh();
  };

  return (
    <div className="max-w-3xl" data-testid="admin-section-products">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <select value={shopKey} onChange={(e) => setShopKey(e.target.value)} data-testid="products-shop-select" className="rounded-lg border border-gold/20 bg-dusk/60 px-3 py-2 text-sm text-sand">
          {data.shops.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
        <button data-testid="add-product" onClick={startNew} className="rounded-full bg-gold px-5 py-2 text-sm font-medium text-dusk transition-transform hover:scale-[1.03]">নতুন পণ্য</button>
      </div>
      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-xl border border-gold/15 bg-dusk/50 p-4" data-testid={`product-row-${p.id}`}>
            <img src={p.img} alt="" className="h-12 w-16 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg text-sand">{p.title}</p>
              <p className="truncate text-xs text-sand/50">{p.price} • {p.artisan}</p>
            </div>
            <button data-testid={`edit-product-${p.id}`} onClick={() => startEdit(p)} className="rounded-full border border-gold/30 px-4 py-1 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk">সাধুন</button>
            <button data-testid={`del-product-${p.id}`} onClick={() => del(p.id)} className="rounded-full border border-red-400/30 px-4 py-1 text-xs text-red-300 transition-colors hover:bg-red-400 hover:text-dusk">মুছুন</button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-sand/50">এই ঘরে এখনো পণ্য নেই — নতুন পণ্য যোগ করুন।</p>}
      </div>

      {form && (
        <div className="mt-6 space-y-4 rounded-xl border border-gold/25 bg-plaster p-5">
          <h4 className="font-display text-lg text-gold">{editId ? "পণ্য সাধুন" : "নতুন পণ্য"}</h4>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="পণ্যের নাম" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="product-title" />
            <Field label="ইংরেজি নাম" value={form.latin} onChange={(v) => setForm({ ...form, latin: v })} testid="product-latin" />
            <Field label="দাম" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="product-price" />
            <Field label="শিল্পী" value={form.artisan} onChange={(v) => setForm({ ...form, artisan: v })} testid="product-artisan" />
            <Field label="ট্যাগ" value={form.tag} onChange={(v) => setForm({ ...form, tag: v })} testid="product-tag" />
            <Field label="ক্রম" value={String(form.order)} onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} testid="product-order" />
          </div>
          <ImageField label="পণ্যের ছবি" value={form.img} onChange={(v) => setForm({ ...form, img: v })} />
          <SaveBar saving={false} done={false} onSave={save} testid="save-product" />
          <button onClick={() => { setForm(null); setEditId(null); }} className="ml-3 text-sm text-sand/60 hover:text-sand">বাতিল</button>
        </div>
      )}
    </div>
  );
}

/* ---------- মঞ্চ-সময়সূচি ---------- */
export function Slots({ data, refresh }) {
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const startNew = () => { setEditId(null); setForm({ time: "", performer: "", title: "", note: "", total: 40, order: (data.slots.length + 1) * 10, active: true }); };
  const startEdit = (s) => { setEditId(s.id); setForm({ ...s, total: s.total || 40 }); };

  const save = async () => {
    try {
      if (editId) await adminFetch(`/admin/slots/${editId}`, { method: "PUT", body: JSON.stringify(form) });
      else await adminFetch("/admin/slots", { method: "POST", body: JSON.stringify(form) });
      setForm(null); setEditId(null); refresh();
    } catch (e) { alert(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("সময়টি মুছে যাবে?")) return;
    await adminFetch(`/admin/slots/${id}`, { method: "DELETE" });
    refresh();
  };

  return (
    <div className="max-w-3xl" data-testid="admin-section-slots">
      <button data-testid="add-slot" onClick={startNew} className="mb-5 rounded-full bg-gold px-5 py-2 text-sm font-medium text-dusk transition-transform hover:scale-[1.03]">নতুন সময়</button>
      <div className="space-y-3">
        {data.slots.map((s) => (
          <div key={s.id} className="flex items-center gap-4 rounded-xl border border-gold/15 bg-dusk/50 p-4" data-testid={`slot-row-${s.id}`}>
            <div className="w-24 shrink-0 font-display text-lg text-gold">{s.time}</div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg text-sand">{s.performer} — {s.title}</p>
              <p className="truncate text-xs text-sand/50">আসন: {(s.total || 40) - (s.booked || 0)} খালি / {s.total || 40}</p>
            </div>
            <button data-testid={`edit-slot-${s.id}`} onClick={() => startEdit(s)} className="rounded-full border border-gold/30 px-4 py-1 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk">সাধুন</button>
            <button data-testid={`del-slot-${s.id}`} onClick={() => del(s.id)} className="rounded-full border border-red-400/30 px-4 py-1 text-xs text-red-300 transition-colors hover:bg-red-400 hover:text-dusk">মুছুন</button>
          </div>
        ))}
      </div>

      {form && (
        <div className="mt-6 space-y-4 rounded-xl border border-gold/25 bg-plaster p-5">
          <h4 className="font-display text-lg text-gold">{editId ? "সময় সাধুন" : "নতুন সময়"}</h4>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="সময় (যেমন সন্ধ্যা ৬টা)" value={form.time} onChange={(v) => setForm({ ...form, time: v })} testid="slot-time" />
            <Field label="শিল্পী" value={form.performer} onChange={(v) => setForm({ ...form, performer: v })} testid="slot-performer" />
            <Field label="পরিবেশনা" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="slot-title" />
            <Field label="মোট আসন" value={String(form.total)} onChange={(v) => setForm({ ...form, total: Number(v) || 40 })} testid="slot-total" />
          </div>
          <Field label="বিশেষ কথা" value={form.note} onChange={(v) => setForm({ ...form, note: v })} testid="slot-note" area />
          <SaveBar saving={false} done={false} onSave={save} testid="save-slot" />
          <button onClick={() => { setForm(null); setEditId(null); }} className="ml-3 text-sm text-sand/60 hover:text-sand">বাতিল</button>
        </div>
      )}
    </div>
  );
}

/* ---------- জিজ্ঞেস-ফর্ম ও বুকিং ---------- */
export function Inquiries({ data }) {
  const [items, setItems] = useState(data.enquiries || []);
  useEffect(() => setItems(data.enquiries || []), [data]);

  const mark = async (id, status) => {
    await adminFetch(`/admin/enquiries/${id}/status`, { method: "POST", body: JSON.stringify({ status }) });
    setItems((xs) => xs.map((x) => (x.enquiry_id === id ? { ...x, status } : x)));
  };

  return (
    <div className="max-w-3xl" data-testid="admin-section-enquiries">
      {items.length === 0 && <p className="text-sm text-sand/50">এখনো কোনো জিজ্ঞেস আসেনি।</p>}
      <div className="space-y-3">
        {items.map((q) => (
          <div key={q.enquiry_id} className="rounded-xl border border-gold/15 bg-dusk/50 p-4" data-testid={`enquiry-row-${q.enquiry_id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-display text-lg text-gold">{q.enquiry_id}</span>
              <span className="text-sm text-sand">{q.name} • {q.phone}</span>
              <span className={`ml-auto rounded-full px-3 py-1 text-xs ${q.status === "done" ? "bg-emerald-500/20 text-emerald-300" : "bg-gold/15 text-gold"}`}>
                {q.status === "done" ? "যোগাযোগ হয়েছে" : "নতুন"}
              </span>
              {q.status !== "done" && (
                <button data-testid={`enquiry-done-${q.enquiry_id}`} onClick={() => mark(q.enquiry_id, "done")} className="rounded-full border border-gold/30 px-4 py-1 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk">যোগাযোগ হয়েছে</button>
              )}
            </div>
            <p className="mt-2 text-sm text-sand/75">{q.place} — {q.message}</p>
            <p className="mt-1 text-xs text-sand/45">{(q.items || []).map((i) => `${i.title} ×${i.qty || 1}`).join(", ")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Bookings({ data }) {
  const [items, setItems] = useState(data.bookings || []);
  useEffect(() => setItems(data.bookings || []), [data]);

  const mark = async (code, status) => {
    await adminFetch(`/admin/bookings/${code}/status`, { method: "POST", body: JSON.stringify({ status }) });
    setItems((xs) => xs.map((x) => (x.code === code ? { ...x, status } : x)));
  };

  return (
    <div className="max-w-3xl" data-testid="admin-section-bookings">
      {items.length === 0 && <p className="text-sm text-sand/50">এখনো কোনো বুকিং নেই।</p>}
      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.code} className="flex flex-wrap items-center gap-3 rounded-xl border border-gold/15 bg-dusk/50 p-4" data-testid={`booking-row-${b.code}`}>
            <span className="font-display text-lg text-gold">{b.code}</span>
            <span className="text-sm text-sand">{b.name} • {b.guests} জন • {b.performer}</span>
            <span className={`ml-auto rounded-full px-3 py-1 text-xs ${b.status === "done" ? "bg-emerald-500/20 text-emerald-300" : "bg-gold/15 text-gold"}`}>
              {b.status === "done" ? "হয়ে গেছে" : "নতুন"}
            </span>
            {b.status !== "done" && (
              <button data-testid={`booking-done-${b.code}`} onClick={() => mark(b.code, "done")} className="rounded-full border border-gold/30 px-4 py-1 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk">হয়ে গেছে</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- শিল্পী-হিসাব ---------- */
export function Creators({ data, refresh }) {
  const [items, setItems] = useState(data.creators || []);
  const [form, setForm] = useState(null);
  useEffect(() => setItems(data.creators || []), [data]);

  const add = async () => {
    try {
      await adminFetch("/admin/creators", { method: "POST", body: JSON.stringify(form) });
      setForm(null);
      refresh();
    } catch (e) {
      alert(e.message);
    }
  };

  const del = async (email) => {
    if (!window.confirm(`${email} হিসাবটি মুছে যাবে?`)) return;
    await adminFetch(`/admin/creators/${encodeURIComponent(email)}`, { method: "DELETE" });
    refresh();
  };

  return (
    <div className="max-w-3xl" data-testid="admin-section-creators">
      <p className="mb-4 text-sm text-sand/60">প্রতিটি শিল্পী নিজের ঘর আর পণ্য নিজেই সাধতে পারবেন — তাঁদের প্রবেশ-তথ্য এখান থেকে তৈরি করুন।</p>
      <button data-testid="add-creator" onClick={() => setForm({ email: "", password: "", name: "", shop_key: data.shops[0]?.key || "" })} className="mb-5 rounded-full bg-gold px-5 py-2 text-sm font-medium text-dusk transition-transform hover:scale-[1.03]">
        নতুন শিল্পী-হিসাব
      </button>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.email} className="flex items-center gap-4 rounded-xl border border-gold/15 bg-dusk/50 p-4" data-testid={`creator-row-${c.email}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg text-sand">{c.name || c.email}</p>
              <p className="truncate text-xs text-sand/50">{c.email} • ঘর: {c.shop_key}</p>
            </div>
            <button data-testid={`del-creator-${c.email}`} onClick={() => del(c.email)} className="rounded-full border border-red-400/30 px-4 py-1 text-xs text-red-300 transition-colors hover:bg-red-400 hover:text-dusk">মুছুন</button>
          </div>
        ))}
      </div>

      {form && (
        <div className="mt-6 space-y-4 rounded-xl border border-gold/25 bg-plaster p-5">
          <h4 className="font-display text-lg text-gold">নতুন শিল্পী-হিসাব</h4>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="ইমেল" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="creator-email" />
            <Field label="পাসওয়ার্ড (৮+ অক্ষর)" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="creator-password" />
            <Field label="নাম" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="creator-name" />
            <label className="block">
              <span className="text-xs tracking-wide text-sand/60">কোন ঘর</span>
              <select value={form.shop_key} onChange={(e) => setForm({ ...form, shop_key: e.target.value })} data-testid="creator-shop-select" className={`mt-1 ${inputCls}`}>
                {data.shops.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </label>
          </div>
          <SaveBar saving={false} done={false} onSave={add} testid="save-creator" />
          <button onClick={() => setForm(null)} className="ml-3 text-sm text-sand/60 hover:text-sand">বাতিল</button>
        </div>
      )}
    </div>
  );
}

/* ---------- পাসওয়ার্ড ---------- */
export function PasswordForm() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [msg, setMsg] = useState("");

  const save = async () => {
    try {
      await adminFetch("/admin/password", { method: "POST", body: JSON.stringify({ current: cur, new: nw }) });
      setMsg("পাসওয়ার্ড বদলে গেছে ✓");
      setCur(""); setNw("");
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div className="max-w-md space-y-4" data-testid="admin-section-password">
      <Field label="বর্তমান পাসওয়ার্ড" value={cur} onChange={setCur} testid="password-current" />
      <Field label="নতুন পাসওয়ার্ড (কমপক্ষে ৮ অক্ষর)" value={nw} onChange={setNw} testid="password-new" />
      <SaveBar saving={false} done={false} onSave={save} testid="save-password" />
      {msg && <p data-testid="password-message" className="text-sm text-gold">{msg}</p>}
    </div>
  );
}
